import {TemplateNode} from "#template/template-node/template-node"
import type {ObjectData, ObjectNodeConstructorParameters, ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {OnCompileContext, ObjectPath} from "#template/template-node/types"
import type {BuilderInlet, BuilderOutlet} from "#template/runtime-graph/graph-builder"
import {Matrix4} from "@cm/math"
import type {SolverObjectData} from "#template/runtime-graph/nodes/solver/object-data"
import type {ObjectId, SceneNodes} from "#template/interfaces/scene-object"
import type {ObjectLikeNode} from "#template/nodes/object/fundamental"
import {objectLikeMarker} from "#template/nodes/object/fundamental"
import type {SceneInteractiveNode} from "#template/role-markers"
import {fundamentalLikeMarker, sceneInteractiveMarker} from "#template/role-markers"
import type {INodeSceneInteractionsContext, TemplateNodePart, TransformInteraction} from "#template/interactions"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {SolverData} from "#template/runtime-graph/nodes/solver/data"
import type {ObjectBounds, RawBounds} from "#template/geometry-processing/bounds"
import {Transform, TransformState} from "#template/runtime-graph/nodes/transform"
import {EMPTY_LIST, ThisStructID} from "#template/runtime-graph/types"
import type {TransformAccessorListEntry} from "#template/runtime-graph/nodes/compile-template"
import {isBuilderOutlet} from "#template/runtime-graph/descriptors"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {TransformValue} from "#template/value-types"
import {isNodeWithMatrixArrayLockedTransformParameter} from "#template/utils/transform-utils"

const TD = TypeDescriptors

const sceneNodeArrayDesc = TD.Array(TD.Identity<SceneNodes.SceneNode>())

export abstract class ObjectNode<
        out Parameters extends ObjectNodeParameters = ObjectNodeParameters,
        out ConstructorParameters extends ObjectNodeConstructorParameters = Omit<Parameters, "visible"> & {visible?: boolean},
    >
    extends TemplateNode<Parameters, ConstructorParameters>
    implements ObjectLikeNode, SceneInteractiveNode
{
    get [objectLikeMarker]() {
        return true as const
    }
    get [fundamentalLikeMarker]() {
        return true as const
    }
    get [sceneInteractiveMarker]() {
        return true as const
    }

    public abstract override get definition(): ObjectNodeDefinition<Parameters, ConstructorParameters>

    public evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ObjectData | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<ObjectData>(`objectData-${evaluator.getLocalId(this)}`))
    }

    canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return true
    }

    canTransformPart(_part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): boolean {
        return isNodeWithMatrixArrayLockedTransformParameter(this) && context.getTransformAccessor(this) !== undefined
    }

    beginTransformInteraction(_part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): TransformInteraction | undefined {
        if (!isNodeWithMatrixArrayLockedTransformParameter(this)) return undefined
        const node = this as TemplateNode<{lockedTransform: TransformValue}>
        const accessor = context.getTransformAccessor(node)
        const startWorld = accessor?.getTransform()
        if (!accessor || !startWorld) return undefined
        const startLocked = Matrix4.fromArray(node.parameters.lockedTransform)
        let lastWorld: Matrix4 | undefined
        return {
            update: (world) => {
                lastWorld = world
                accessor.setTransform(world, true)
            },
            commit: () => {
                accessor.endTransform()
                if (!lastWorld || lastWorld.equals(startWorld)) return
                node.updateParameters({lockedTransform: lastWorld.multiply(startWorld.inverse()).multiply(startLocked).toArray()})
            },
            cancel: () => {
                accessor.setTransform(startWorld, true)
                accessor.endTransform()
            },
        }
    }

    getPartTransform(_part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): Matrix4 {
        return context.getTransformAccessor(this)?.getTransform() ?? Matrix4.identity()
    }

    isDropTargetFor(_dropSourceNode: TemplateNode, _dropTargetPart: TemplateNodePart["part"]): boolean {
        return false
    }

    handleDroppedSource(_dropSourceNode: TemplateNode, _dropTargetPart: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): void {}

    setupTransform(
        scope: GraphBuilderScope,
        context: OnCompileContext,
        transform: BuilderInlet<Matrix4 | undefined> | undefined,
    ): readonly [BuilderOutlet<Matrix4>, BuilderOutlet<SolverObjectData>, BuilderOutlet<boolean>] {
        const {evaluator, currentTemplate} = context
        const {solverData, transformAccessorList} = currentTemplate
        const {templateContext} = evaluator
        const {templateMatrix} = templateContext
        const {sceneManager} = templateContext
        const {lockedTransform: lockedTransformParam} = this.parameters
        const {solverObjects} = solverData

        const lockedTransform = lockedTransformParam ? evaluator.evaluate(lockedTransformParam) : null

        const transformNode = scope.node(Transform, {
            state: scope.state(TransformState, "transformState"),
            defaultTransform: sceneManager.defaultTransformForObject(this) ?? null,
            lockedTransform: scope.pureLambda(
                scope.tuple(transform, lockedTransform),
                ([transform, lockedTransform]) => {
                    if (transform && lockedTransform) return transform.multiply(Matrix4.fromArray(lockedTransform))
                    return transform ?? (lockedTransform ? Matrix4.fromArray(lockedTransform) : null)
                },
                "lockedTransform",
            ),
            parentMatrix: scope.pureLambda(
                scope.tuple(templateMatrix, transform),
                ([templateMatrix, transform]) => (transform === undefined ? templateMatrix : Matrix4.identity()),
                "parentMatrix",
            ),
        })

        const solverObject = scope.struct<SolverObjectData>("SolverObjectData", {
            id: ThisStructID,
            transformAccessor: transformNode.accessor,
        })
        solverObjects.push(solverObject)

        transformAccessorList.push(
            scope.struct<TransformAccessorListEntry>("TransformAccessorListEntry", {
                templateNode: this,
                transformAccessor: transformNode.accessor,
            }),
        )

        return [transformNode.matrix, solverObject, transformNode.isInteracting] as const
    }

    setupObject(
        scope: GraphBuilderScope,
        context: OnCompileContext,
        typeName: string,
        bounds: BuilderInlet<RawBounds> | undefined,
        parent:
            | BuilderInlet<{
                  id: ObjectId
                  matrix: Matrix4
                  visible: boolean
              } | null>
            | undefined,
        generator: (
            objectProps: {
                $id: ObjectId
                id: ObjectId
                transform: BuilderInlet<Matrix4>
                isInteracting: BuilderInlet<boolean>
                path: BuilderInlet<ObjectPath>
            },
            otherData: {
                solverObject: BuilderOutlet<SolverObjectData>
                solverData: BuilderOutlet<SolverData>
                visible: BuilderOutlet<boolean>
            },
        ) =>
            | BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[]>
            | {
                  displayList?: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[]>
                  preDisplayList?: BuilderOutlet<SceneNodes.SceneNode | SceneNodes.SceneNode[]>
              },
        invalidator?: BuilderOutlet<null>,
    ) {
        const {evaluator, currentTemplate, path} = context
        const {allBounds} = currentTemplate
        const {preDisplayLists, displayLists} = context.subTemplates
        const {templateScope} = evaluator
        const {visible: thisVisible} = this.parameters

        const objectId = scope.genStructId(typeName)

        const [transformMatrix, solverObject, isInteracting] = this.setupTransform(scope, context, scope.get(parent, "matrix"))

        let objectBounds: BuilderInlet<ObjectBounds> | undefined
        if (bounds) {
            objectBounds = scope.pureLambda(
                scope.tuple(bounds, transformMatrix, objectId),
                ([bounds, transformMatrix, objectId]): ObjectBounds => {
                    return {...bounds, objectId, matrix: transformMatrix.toArray()}
                },
                "getBounds",
            )

            allBounds.push(objectBounds)
        }

        const visible = scope.pureLambda(
            scope.tuple(thisVisible, parent),
            ([thisVisible, parentVisible]) => thisVisible && (parentVisible?.visible ?? true),
            "visible",
        )

        const solverData = scope.struct<SolverData>("SolverData", {
            objects: scope.pureLambda(solverObject, (solverObject) => [solverObject], "solverObject"),
            relations: EMPTY_LIST,
            variables: EMPTY_LIST,
        })

        const newPath = scope.pureLambda(scope.tuple(path, this), ([path, instance]) => [...path, instance], "newPath", {
            output: TD.ObjectPath,
        })

        const generatorOutput = generator(
            {
                $id: objectId,
                id: objectId,
                transform: transformMatrix,
                isInteracting,
                path: newPath,
            },
            {
                solverObject,
                solverData,
                visible,
            },
        )

        const preDisplayList = isBuilderOutlet(generatorOutput)
            ? (EMPTY_LIST as SceneNodes.SceneNode[])
            : (generatorOutput.preDisplayList ?? (EMPTY_LIST as SceneNodes.SceneNode[]))
        const displayList = isBuilderOutlet(generatorOutput) ? generatorOutput : (generatorOutput.displayList ?? (EMPTY_LIST as SceneNodes.SceneNode[]))

        const invalidatorFallback = scope.pureLambda(invalidator, (invalidator) => EMPTY_LIST as SceneNodes.SceneNode[], "sceneNodesInvalidator")

        const preDisplaySceneNode = scope.pureLambda(
            invalidator === undefined ? preDisplayList : scope.phi(invalidatorFallback, preDisplayList),
            (sceneNode) => (Array.isArray(sceneNode) ? sceneNode : [sceneNode]),
            "preDisplaySceneNodes",
            {
                output: sceneNodeArrayDesc,
            },
        )

        const displaySceneNode = scope.pureLambda(
            invalidator === undefined ? displayList : scope.phi(invalidatorFallback, displayList),
            (sceneNode) => (Array.isArray(sceneNode) ? sceneNode : [sceneNode]),
            "displaySceneNodes",
            {
                output: sceneNodeArrayDesc,
            },
        )

        preDisplayLists.push(
            scope.pureLambda(
                scope.tuple(visible, preDisplaySceneNode),
                ([visible, preDisplaySceneNode]) => (visible ? preDisplaySceneNode : EMPTY_LIST),
                "preDisplayList",
            ),
        )
        displayLists.push(
            scope.pureLambda(scope.tuple(visible, displaySceneNode), ([visible, displaySceneNode]) => (visible ? displaySceneNode : EMPTY_LIST), "displayList"),
        )

        const objectData = scope.struct<ObjectData>("ObjectData", {
            id: objectId,
            path: newPath,
            matrix: transformMatrix,
            solverObject,
            bounds: objectBounds,
            dimensionGuideBounds: objectBounds,
            preDisplayList: preDisplaySceneNode,
            displayList: displaySceneNode,
            solverData,
            visible,
        })

        templateScope.alias(invalidator ? scope.phi(objectData, invalidator) : objectData, `objectData-${evaluator.getLocalId(this)}`)
    }
}
